import Load from 'components/Load';
import {
  NavigationalLayoutElement,
  type NavigationalLayoutProps,
} from 'modules/app/components/NavigationalLayout';
import AppBottomPushDown from 'modules/common/Navbar/PushDown/AppBottomPushDown';
import Notfound from 'modules/errorpages/Notfound';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { RouteDescriptor, createRouteHandle } from 'routing/types';
import basePath from 'utils/basePath';
import { isCordova } from 'utils/platform';
import { relativePathFromBase } from 'utils/relativePathFromBase';

const AsyncOverviewPage = Load(
  () =>
    import(
      /* webpackChunkName: "learning-center-overview" */ './components/OverviewPage'
    ),
);
const AsyncCollectionPage = Load(
  () =>
    import(
      /* webpackChunkName: "learning-center" */ './containers/CollectionPageContainer'
    ),
);
const AsyncPreviewPage = Load(
  () =>
    import(
      /* webpackChunkName: "learning-center" */ './components/PreviewPage'
    ),
);
const AsyncSingleArticlePage = Load(
  () =>
    import(
      /* webpackChunkName: "learning-center" */ './components/SingleArticlePage'
    ),
);
const AsyncPreviewCollectionPage = Load(
  () =>
    import(
      /* webpackChunkName: "learning-center" */ './components/PreviewCollectionPage'
    ),
);
const AsyncVideoArticlePage = Load(
  () =>
    import(
      /* webpackChunkName: "learning-center" */ './containers/VideoArticlePageContainer'
    ),
);
const AsyncCoursePage = Load(
  () =>
    import(
      /* webpackChunkName: "learning-center-course" */ './containers/CoursePageContainer'
    ),
);

const base = `${basePath}/learning-center`;
type ChildRouteOptions = Omit<NavigationalLayoutProps, 'component'> & {
  path?: string;
  index?: boolean;
};

const createChildRoute = (
  descriptor: RouteDescriptor,
  Component: React.ComponentType<any>,
  options: ChildRouteOptions = {},
) => {
  const { path: override, index: forcedIndex, ...layoutProps } = options;
  const relative = override ?? relativePathFromBase(base, descriptor.path);
  const isIndex = forcedIndex ?? relative === '';

  const element = (
    <NavigationalLayoutElement {...layoutProps} component={Component as any} />
  );

  if (isIndex) {
    return (
      <Route
        key={`${descriptor.id}__index`}
        id={descriptor.id}
        index
        handle={createRouteHandle(descriptor)}
        element={element}
      />
    );
  }

  return (
    <Route
      key={descriptor.id}
      id={descriptor.id}
      path={relative}
      handle={createRouteHandle(descriptor)}
      element={element}
    />
  );
};

export const hostLearningCenterRootRoute: RouteDescriptor = {
  id: 'host-learning-center.root',
  path: `${base}/*`,
  label: 'Host Learning Center Root',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterCourseRoute: RouteDescriptor = {
  id: 'host-learning-center.course',
  path: `${base}/course-details/:id`,
  label: 'Learning Center Course Details',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterCourseStartRoute: RouteDescriptor = {
  id: 'host-learning-center.course.start',
  path: `${base}/course/start/:id`,
  label: 'Learning Center Course Start',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterCourseCompletedRoute: RouteDescriptor = {
  id: 'host-learning-center.course.completed',
  path: `${base}/course/completed/:id`,
  label: 'Learning Center Course Completed',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterVideoRoute: RouteDescriptor = {
  id: 'host-learning-center.video',
  path: `${base}/video/:id`,
  label: 'Learning Center Video',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterCollectionRoute: RouteDescriptor = {
  id: 'host-learning-center.collection',
  path: `${base}/:category/:id`,
  label: 'Learning Center Collection',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterArticleRoute: RouteDescriptor = {
  id: 'host-learning-center.article',
  path: `${base}/article/:category/:id`,
  label: 'Learning Center Article',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterPreviewArticleRoute: RouteDescriptor = {
  id: 'host-learning-center.preview-article',
  path: `${base}/preview-article/:category/:id`,
  label: 'Learning Center Preview Article',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterPreviewCollectionRoute: RouteDescriptor = {
  id: 'host-learning-center.preview-collection',
  path: `${base}/preview-collection/:id`,
  label: 'Learning Center Preview Collection',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

export const hostLearningCenterOverviewRoute: RouteDescriptor = {
  id: 'host-learning-center.overview',
  path: base,
  label: 'Learning Center Overview',
  audience: 'host',
  layout: 'navigational',
  module: 'host-learning-center',
};

const SubRoutes = () => {
  return (
    <Routes>
      {createChildRoute(hostLearningCenterCourseRoute, AsyncCoursePage, {
        hideFooter: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(hostLearningCenterCourseStartRoute, AsyncCoursePage, {
        hideFooter: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(
        hostLearningCenterCourseCompletedRoute,
        AsyncCoursePage,
        {
          hideFooter: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(hostLearningCenterVideoRoute, AsyncVideoArticlePage, {
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(
        hostLearningCenterCollectionRoute,
        AsyncCollectionPage,
        {
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(
        hostLearningCenterArticleRoute,
        AsyncSingleArticlePage,
        {
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(
        hostLearningCenterPreviewArticleRoute,
        AsyncPreviewPage,
        {
          hideFooter: true,
          noNavbar: isCordova(),
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(
        hostLearningCenterPreviewCollectionRoute,
        AsyncPreviewCollectionPage,
        {
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(hostLearningCenterOverviewRoute, AsyncOverviewPage, {
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
        showBottomNavigation: true,
        index: true,
      })}
      <Route path="*" element={<Notfound />} />
    </Routes>
  );
};

const HostLearningCenterRoutes = () => (
  <>
    <SubRoutes />
    <AppBottomPushDown />
  </>
);

export const hostLearningCenterRoutes = [
  <Route
    key={hostLearningCenterRootRoute.id}
    id={hostLearningCenterRootRoute.id}
    path={hostLearningCenterRootRoute.path}
    handle={createRouteHandle(hostLearningCenterRootRoute)}
    element={<HostLearningCenterRoutes />}
  />,
];

export const hostLearningCenterRouteDescriptors = [
  hostLearningCenterRootRoute,
  hostLearningCenterCourseRoute,
  hostLearningCenterCourseStartRoute,
  hostLearningCenterCourseCompletedRoute,
  hostLearningCenterVideoRoute,
  hostLearningCenterCollectionRoute,
  hostLearningCenterArticleRoute,
  hostLearningCenterPreviewArticleRoute,
  hostLearningCenterPreviewCollectionRoute,
  hostLearningCenterOverviewRoute,
];

export default hostLearningCenterRoutes;
